import React, { useContext, useState, useEffect } from 'react'
import { Typography, FormControl, FormHelperText, Tooltip, Box, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles';
import { DataRoomsContext, MessageContext } from './Contexts'
import { UpdateRoom } from 'modules/Rest'
import { format } from 'date-fns'
import AButton from './AButton'
import { setModes } from 'modules/Utils'
import ActionButtons, { ControlledActionButton } from './ActionButtons'
import { useNavigate } from 'react-router'
import { useSelectedDataRoom } from 'hooks/useSelectedDataRoom'

const useStyles = makeStyles(theme => ({
  FormControl: {
    flex: 1,
    display: 'unset',
    textAlign: 'center',
    '& p': {textAlign: 'center'},
  },
  dialogContent: {
    width: 400,
  },
  inputMargin: {
    marginTop: 12,
  },
}))

function DataRoomActionsCard() {
  const { setRefreshNext } = useContext(DataRoomsContext)
  const { dataRoom } = useSelectedDataRoom()
  const { messageDispatch } = useContext(MessageContext)
  const [disabled, setDisabled] = useState(dataRoom.disabled)
  const [uploadDatesResettable, setUploadDatesResettable] = useState(false)
  const [uploadDatesResetDisabled, disableUploadDatesReset] = useState(false)
  const [updateReset, setUpdateReset] = useState(dataRoom.update_reset)
  const [password, setPassword] = useState('')
  const classes = useStyles()

  const [modeResetRecent, setModeResetRecent] = useState('')
  const [modeResetUploadDates, setModeResetUploadDates] = useState('')
  const [modeDisable, setModeDisable] = useState('')
  const [modeDelete, setModeDelete] = useState('')

  const navigate = useNavigate()

  useEffect(() => {
    setUpdateReset(dataRoom.update_reset)
  }, [dataRoom.data_room, dataRoom.update_reset])

  const resetRecent = async () => {
    try {
      setModeResetRecent('pending')
      const { data: { success, info } } = await UpdateRoom({
        data_room: dataRoom.data_room,
        update_reset: true,
      })
      if (success) {
        await setModes(setModeResetRecent, ['completing', 1500, ''])
        setUpdateReset(new Date())
        setRefreshNext(true)
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setModeResetRecent('')
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }

  const resetUploadDates = async () => {
    try {
      setModeResetUploadDates('pending')
      const { data: { success, info } } = await UpdateRoom({
        data_room: dataRoom.data_room,
        update_upload_dates: true,
      })
      if (success) {
        await setModes(setModeResetUploadDates, ['completing', 1500, ''])
        setUploadDatesResettable(false)
        disableUploadDatesReset(true)
        setRefreshNext(true)
        messageDispatch({type: 'info', payload: 'Upload dates are being reset'})
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setModeResetUploadDates('')
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }

  const disableDataRoom = async () => {
    try {
      setModeDisable('pending')
      const { data: { success, info } } = await UpdateRoom({
        data_room: dataRoom.data_room,
        disable: !disabled,
      })
      if (success) {
        await setModes(setModeDisable, ['completing', 1500, ''])
        setRefreshNext(true)
        setDisabled(!disabled)
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setModeDisable('')
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }

  const deleteDataRoom = async () => {
    try {
      setModeDelete('pending')
      const { data: { success, info } } = await UpdateRoom({
        data_room: dataRoom.data_room,
        delete: true,
        password,
      })
      if (success) {
        await setModes(setModeDelete, ['completing', 1500, ''])
        setModeDelete('')
        setRefreshNext(true)
        navigate('/data-rooms')
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setModeDelete('')
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }

  return (
    <>
      <Box display='flex' flexDirection='row'>
        <FormControl className={classes.FormControl}>
          <Typography variant='subtitle1'>
            Recently updated files
          </Typography>

          <ControlledActionButton
            mode={modeResetRecent}
            setMode={setModeResetRecent}
            action={resetRecent}
            actionTitle='Reset list'
          />
          <FormHelperText>
            {updateReset > -1 ? `Last reset of list: ${format(new Date(updateReset), 'yyyy-MM-dd kk:mm')}` : 'No reset'}
          </FormHelperText>
        </FormControl>
        <FormControl className={classes.FormControl}>
          <Typography variant='subtitle1'>
            Upload dates
          </Typography>
          {!uploadDatesResettable && (
            <Tooltip title='Sets the upload date of all files to the current date and time'>
              <AButton
                disabled={uploadDatesResetDisabled}
                onClick={() => setModeResetUploadDates('editing')}
              >
                Reset upload dates
              </AButton>
            </Tooltip>
          )}
          <Dialog open={!!modeResetUploadDates}>
            <DialogTitle>Confirmation request</DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <Typography variant='body2'>
                Would you like to set the upload date of all files in the data room to the current date and time?
              </Typography>
              <Typography variant='body2'>
                This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <ActionButtons
                mode={modeResetUploadDates}
                setMode={setModeResetUploadDates}
                action={resetUploadDates}
                cancel={(e, setMode) => setMode('')}
                actionTitle='Reset'
                colorAction='error'
              />
            </DialogActions>
          </Dialog>
          <FormHelperText>Reset the upload date for all files.</FormHelperText>
        </FormControl>
        <FormControl className={classes.FormControl}>
          <Typography variant='subtitle1'>
            Disable
          </Typography>
          <ControlledActionButton
            mode={modeDisable}
            setMode={setModeDisable}
            action={disableDataRoom}
            actionTitle={disabled ? 'Enable' : 'Disable'}
          />
          <FormHelperText>A disabled data room may only be accessed by administrators.</FormHelperText>
        </FormControl>
        <FormControl className={classes.FormControl}>
          <Typography variant='subtitle1'>
            Delete
          </Typography>
          <AButton onClick={() => setModeDelete('editing')} color="error">
            Delete
          </AButton>
          <FormHelperText>A deleted data room can be recovered within 30 days.</FormHelperText>
        </FormControl>

        <Dialog open={!!modeDelete} disableRestoreFocus>
          <DialogTitle>Do you want do delete the data room?</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Typography variant='body2'>
              A deleted data room can be recovered within 30 days.
            </Typography>
            <Typography variant='body2'>
              Enter your password below to confirm the deletion.
            </Typography>
            <TextField
              type='password'
              label='Password'
              variant='standard'
              value={password}
              onChange={event => setPassword(event.target.value)}
              onKeyPress={({ key }) => key === 'Enter' && deleteDataRoom()}
              autoFocus
              className={classes.inputMargin}
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <ActionButtons
              mode={modeDelete}
              setMode={setModeDelete}
              action={deleteDataRoom}
              cancel={(e, setMode) => setMode('')}
              actionTitle='Delete'
              colorAction='error'
              neverShowEdit={true}
            />
          </DialogActions>
        </Dialog>
      </Box>
    </>
  )
}

export default DataRoomActionsCard
